import { useState, useCallback } from 'react';
import { useImageProcessor } from './useImageProcessor';
import { ImageMetadata } from '../../../types/image-preview';

export interface PreviewState {
  originalMetadata: ImageMetadata | null;
  previewError: string | null;
  showPreview: boolean;
}

export const useImageProcessorWithPreview = () => {
  // 获取基础Hook的所有功能
  const baseProcessor = useImageProcessor();
  
  // 预览相关状态
  const [previewState, setPreviewState] = useState<PreviewState>({
    originalMetadata: null,
    previewError: null,
    showPreview: true
  });

  // 处理图片元数据回调
  const onImageMetadata = useCallback((metadata: ImageMetadata) => {
    setPreviewState(prev => ({
      ...prev,
      originalMetadata: metadata,
      previewError: null
    }));
  }, []);

  // 处理预览错误
  const onPreviewError = useCallback((error: string) => {
    setPreviewState(prev => ({
      ...prev,
      previewError: error
    }));
  }, []);

  // 切换预览显示
  const togglePreview = useCallback(() => {
    setPreviewState(prev => ({
      ...prev,
      showPreview: !prev.showPreview
    }));
  }, []);

  // 清理预览状态
  const clearPreview = useCallback(() => {
    setPreviewState({
      originalMetadata: null,
      previewError: null,
      showPreview: true
    });
  }, []);

  // 扩展的文件设置方法
  const setFileWithPreview = useCallback((file: File | null) => {
    baseProcessor.setFile(file);
    if (!file) {
      clearPreview();
    }
  }, [baseProcessor, clearPreview]);

  // 扩展的重置方法
  const resetWithPreview = useCallback(() => {
    baseProcessor.reset();
    clearPreview();
  }, [baseProcessor, clearPreview]);

  // 返回扩展的Hook接口
  return {
    // 继承基础Hook的所有功能
    ...baseProcessor,
    
    // 预览相关状态和方法
    previewState,
    onImageMetadata,
    onPreviewError,
    togglePreview,
    clearPreview,
    
    // 重写的方法以支持预览
    setFile: setFileWithPreview,
    reset: resetWithPreview
  };
};

export default useImageProcessorWithPreview;